import React, { useState, useEffect } from "react";
import { Card, Button, Tabs, Modal, Spin, Progress } from "antd";
import {
  WhatsAppOutlined,
  TwitterOutlined,
  FacebookOutlined,
  MailOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import DonationCorousel from "./DonationCorousel";
import NotFound from "../../Pages/error/NotFound";
import FundraiserCard from "./FundraiserCard";
import PaymentModal from "./PaymentModal";

const { TabPane } = Tabs;

const DonationPage = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [amount, setAmount] = useState(500);

  const { slug } = useParams();

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/campaign/${slug}?type=slug`
        );
        setCampaignData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch campaign data:", error);
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [slug]);

  const showPaymentModal = () => setIsPaymentModalOpen(true);

  const closePaymentModal = () => setIsPaymentModalOpen(false);
  
  const onTabChange = (key) => setActiveTab(key);

  const showModal = () => setIsModalVisible(true);

  const handleCancel = () => setIsModalVisible(false);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin />
      </div>
    );
  }

  if (!campaignData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <NotFound />
      </div>
    );
  }

  const { name, cover, goal, statistics, description, documents } =
    campaignData;

  return (
    <div className="flex flex-col items-center bg-gray-50 min-h-screen py-10">
      {/* Header Section */}
      <div className="text-center mt-14">
        <h1 className="text-4xl font-extrabold text-blue-800 mb-6 tracking-wide">
          {name}
        </h1>
      </div>

      {/* Campaign Cover */}
      <div className="mb-6 w-full">
        {cover.length > 0 ? (
          <DonationCorousel data={cover} />
        ) : (
          <div className="w-full h-[26vw] bg-gray-300 flex items-center justify-center">
            No cover image available
          </div>
        )}
      </div>

      {/* Campaign Overview */}
      <div className="w-11/12 bg-white p-8 rounded-lg shadow-lg mb-10">
        <div className="flex flex-col lg:flex-row lg:justify-between">
          <div className="mb-6 lg:mb-0">
            <h2 className="text-xl font-semibold mb-2">
              Fundraising by {name}
            </h2>
            {/* <p className="text-gray-600">
              {description.replace(/<\/?[^>]+(>|$)/g, "")}
            </p> */}
          </div>
          <div className="flex gap-4">
            <Button type="primary" className="bg-btn-1 text-white" onClick={showPaymentModal}>
              Donate
            </Button>
            <PaymentModal isOpen={isPaymentModalOpen} onClose={closePaymentModal} amount={amount} />
            <Button
              type="default"
              className="bg-gray-100 text-blue-600 border-blue-600"
            >
              Fundraise for us
            </Button>
            <Button type="dashed" onClick={showModal}>
              Share
            </Button>
          </div>
        </div>
        <div className="mt-6">
          <Progress
            showInfo={false}
            percent={(statistics.raised / goal) * 100}
          />
          <div className="mt-2 text-center">
            <p className="text-3xl font-bold text-gray-700">
              {Math.round((statistics.raised / goal) * 100)}%
            </p>
          </div>
          <div className="flex justify-between">
            <FundraiserCard campaignData={campaignData} />
            <p className="mt-2 text-gray-600">
              Raised: ₹{statistics.raised} / ₹{goal}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Section */}
      <div className="w-11/12 bg-white rounded-lg shadow-lg p-6 mb-10">
        <Tabs activeKey={activeTab} onChange={onTabChange}>
          <TabPane tab="About" key="1">
            <p>{description.replace(/<\/?[^>]+(>|$)/g, "")}</p>
          </TabPane>
          <TabPane tab="Donations" key="2">
            {statistics.raised > 0 ? (
              <p>
                Raised: ₹{statistics.raised}
                <br />
                Donors: {statistics.donors}
              </p>
            ) : (
              <p>
                No donations have been made yet. Be the first to contribute and
                make a difference!
              </p>
            )}
          </TabPane>

          <TabPane tab="Comments" key="3">
            {campaignData.comments && campaignData.comments.length > 0 ? (
              campaignData.comments.map((comment, index) => (
                <p key={index}>{comment.text}</p>
              ))
            ) : (
              <p>
                No comments yet. Be the first to share your thoughts and support
                this cause!
              </p>
            )}
          </TabPane>

          <TabPane tab="Documents" key="4">
            {documents.length > 0 ? (
              documents.map((doc, index) => (
                <a target="_blank" href={campaignData.documents} key={index}>
                  {campaignData.documents}{" "}
                </a>
              ))
            ) : (
              <p>No documents available.</p>
            )}
          </TabPane>
        </Tabs>
      </div>

      {/* Additional Resources */}
      <div className="w-11/12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((item) => (
          <Card
            key={item}
            className="shadow-lg rounded-lg flex flex-col justify-between"
          >
            <img
              src="https://via.placeholder.com/300"
              alt={`Card ${item}`}
              className="mb-4 w-full h-48 object-cover rounded-lg"
            />
            <Button type="primary" className="self-center bg-btn-1">
              Learn More
            </Button>
          </Card>
        ))}
      </div>

      {/* Share Modal */}
      <Modal
        title="Share this fundraiser"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <div className="flex flex-col items-center space-y-4">
          <Button icon={<WhatsAppOutlined />} className="w-full">
            Share on WhatsApp
          </Button>
          <Button icon={<TwitterOutlined />} className="w-full">
            Share on Twitter
          </Button>
          <Button icon={<FacebookOutlined />} className="w-full">
            Share on Facebook
          </Button>
          <Button icon={<MailOutlined />} className="w-full">
            Share via Email
          </Button>
          <Button icon={<LinkOutlined />} className="w-full">
            Copy Link
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DonationPage;
